import { graphql, PageProps } from "gatsby";
import React from "react";
import { format, addDays } from "date-fns";

import { DEFAULT_LANGUAGE } from "@config/consts";
import { HOMEPAGE_VOUCHER_ENDPOINT } from "@config/endpoints";

import { AVAILABLE_COUNTRIES, ORIGIN } from "@typings/globals";

import { langFromTag } from "@services/LangFromTag";
import { buildUrl } from "@services/BuildUrl";
import { DATE_FORMAT } from "@services/DateFormatter";

import { showToast } from "@containers/Toastify";
import { SmartlookClient } from "@containers/SmartlookClient";

import { useVoucherManager } from "@hooks/useVoucherManager";
import { useTelemetry } from "@hooks/useTelemetry";

import type { CitesStaticHomePageQueryQuery } from "@www/graphql-types";
import { asyncActionCreator } from "@www/src/services/AsyncActionCreator";

import UTMCatcher from "../../components/UTMCatcher";
import SEO, { SeoProps } from "../../components/SEO";
import Layout from "../../components/Layout";

import { Bento } from "./Bento";
import { Statement } from "./Statement";
import { BecomeBatmaid } from "./BecomeBatmaid";
import { Services } from "./Services";
import { Features } from "./Features";
import { Extras } from "./Extras";
import { Hero } from "./Hero";
import { Reviews } from "./Reviews";
import { Cities } from "./Cities";
import { HowItWorks } from "./HowItWorks";

type Props = PageProps<
  CitesStaticHomePageQueryQuery,
  {
    language: string;
    country: AVAILABLE_COUNTRIES;
    origin: ORIGIN;
    id: string;
  }
>;

const HomePageV2: React.FC<Props> = ({ pageContext, data, location }) => {
  const language = langFromTag(pageContext.language) || DEFAULT_LANGUAGE;
  const voucherManager = useVoucherManager();
  const { pageView } = useTelemetry();

  React.useEffect(() => {
    pageView({
      pageId: "homepage",
    });
  }, []);

  React.useEffect(() => {
    (async () => {
      const voucherId = voucherManager.getStoredDeeplinkVoucher();

      if (!voucherId) return;

      const { onError, onSuccess } = await asyncActionCreator({
        url: buildUrl(HOMEPAGE_VOUCHER_ENDPOINT)
          .setQuery("voucherToken", voucherId)
          .setQuery(
            "firstJobExecutionDate",
            format(
              addDays(new Date(), 2),
              DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND_ISO_8601,
            ),
          )
          .done(),
        action: "",
      });

      onSuccess(payload => {
        showToast(payload.data.translatedSuccessMessage, {
          type: "success",
        });
      });

      onError(err => {
        showToast(err.message, { type: "error" });
        voucherManager.removeStoredDeeplinkVoucher();
      });
    })();
  }, []);

  const seoData: SeoProps = {
    seo_title: data.prismicLandingPage?.data.seo_title,
    seo_description: data.prismicLandingPage?.data.seo_description,
    seo_image: data.prismicLandingPage?.data.seo_image,
  };

  return (
    <Layout
      language={language}
      country={pageContext.country}
      origin={pageContext.origin}
      isFullWidth
    >
      <SmartlookClient />
      <UTMCatcher />
      <SEO
        withHreflangs
        title={data.prismicLandingPage?.data.title.text || ""}
        lang={language}
        path={location.pathname}
        {...seoData}
      />
      <Hero language={language} />
      <Statement />
      <Bento language={language} country={pageContext.country} />
      <Services />
      <Extras />
      <HowItWorks />
      <Reviews />
      <Features language={language} />
      <Cities query={data} />
      <BecomeBatmaid language={language} />
    </Layout>
  );
};

export const query = graphql`
  query CitesStaticHomePageQuery($id: String) {
    prismicLandingPage(id: { eq: $id }) {
      lang
      data {
        title {
          text
        }
        # ==== SEO ====
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          gatsbyImageData(width: 1200)
        }
        country
        enjoy_service_title {
          text
        }
        enjoy_service_cities {
          image_caption {
            text
          }
          city_image {
            alt
            gatsbyImageData(
              layout: FULL_WIDTH
              width: 334
              imgixParams: { q: 95, fit: "crop", crop: "faces" }
              placeholderImgixParams: { q: 95, fit: "crop", crop: "faces" }
              placeholder: BLURRED
            )
          }
          service_link {
            slug
          }
        }
      }
    }
  }
`;

export default HomePageV2;
